import { Box, Stack, Typography } from '@mui/material';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { Editor, EditorContent, JSONContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import Card from '../Card';
import { CommentManager, CommentMark, useComments } from './CommentExtension';

export type TipTapJson = JSONContent;

export interface TipTapEditorProps {
  initialContent?: string;
  onChange?: (textContent: string, tipTapJsonContent: TipTapJson) => void;
  label?: string;
  readOnly?: boolean;
  placeholder?: string;
  highlightedTextList?: Array<{ quotedText: string, comment: string, type?: 'positive' | 'negative' | 'neutral', id: string }>;
  onCharacterCountChange?: (characterCount: number, wordCount: number) => void;
  editorRef?: RefObject<Editor | null>;
}

interface InnerEditorProps extends Pick<TipTapEditorProps, 'initialContent' | 'onChange' | 'readOnly' | 'placeholder' | 'onCharacterCountChange'> {
  editorRef: RefObject<Editor | null>;
  onHighlightClick?: (id: string) => void;
}

// Helper function to try parsing JSON content
const tryParseJSON = (content: string) => {
  try {
    return JSON.parse(content);
  } catch (error) {
    return null;
  }
};

// Helper function to filter out comment marks from JSON content
const filterOutCommentMarks = (json: TipTapJson): TipTapJson => {
  if (!json) return json;

  const filterNode = (node: any): any => {
    if (!node) return node;

    // Create a new node without the comment marks
    const newNode = { ...node };

    if (newNode.marks) {
      newNode.marks = newNode.marks.filter((mark: any) => mark.type !== 'comment');
      if (newNode.marks.length === 0) {
        delete newNode.marks;
      }
    }

    if (newNode.content && Array.isArray(newNode.content)) {
      newNode.content = newNode.content.map(filterNode);
    }

    return newNode;
  };

  return filterNode(json);
};

// Inner editor component to isolate the editor instance for better performance
const InnerEditor = ({
  initialContent,
  onChange,
  readOnly,
  placeholder,
  onHighlightClick,
  editorRef,
  onCharacterCountChange,
}: InnerEditorProps) => {
  const parsedContent = useMemo(() =>
    tryParseJSON(initialContent || ''),
    []
  );

  // Use ref to track previous text content to avoid unnecessary onChange calls
  const prevTextContentRef = useRef<string>('');

  // Create extensions array with proper memoization
  const extensions = useMemo(() => {
    // Define the extensions array with 'any' type to accommodate different extension types
    const extensionsArray: any[] = [
      StarterKit,
      Underline.configure({
        HTMLAttributes: {
          class: 'tiptap-underline',
        },
      }),
      // Use our custom CommentMark extension instead of the imported one
      CommentMark.configure({
        HTMLAttributes: {
          class: 'tiptap-comment',
        },
        onCommentActivated: (commentId: string) => {
          if (commentId && onHighlightClick) {
            onHighlightClick(commentId);
          }
        },
      }),
    ];

    if (placeholder) {
      extensionsArray.push(
        Placeholder.configure({
          placeholder,
          showOnlyWhenEditable: true,
        })
      );
    }
    return extensionsArray;
  }, [onHighlightClick, placeholder]);

  // Initialize the editor
  const editor = useEditor({
    extensions,
    content: parsedContent || initialContent || '',
    editable: !readOnly,
    onUpdate: useCallback(({ editor }: { editor: Editor }) => {
      if (onChange) {
        const currentText = editor.getText();
        if (currentText !== prevTextContentRef.current) {
          prevTextContentRef.current = currentText;
          const filteredJSON = filterOutCommentMarks(editor.getJSON());
          onChange(currentText, filteredJSON);
          if (onCharacterCountChange) {
            onCharacterCountChange(currentText.length, currentText.split(' ').length);
          }
        }
      }
    }, [onChange, onCharacterCountChange]),
    // Optimize performance with these settings
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
  });

  // Initialize the prevTextContentRef and counts with the initial content
  useEffect(() => {
    if (editor) {
      prevTextContentRef.current = editor.getText();
      if (onCharacterCountChange) {
        onCharacterCountChange(editor.getText().length, editor.getText().split(' ').length);
      }
    }
  }, [editor, onCharacterCountChange]);

  useEffect(() => {
    if (editorRef) {
      editorRef.current = editor;
    }
  }, [editorRef, editor]);

  return (
    <Box sx={{
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      flex: 3,
    }}>
      <EditorContent editor={editor} style={{ flex: 1, display: 'flex', flexDirection: 'column', }} />
    </Box>
  );
};

// Outer component that doesn't re-render when editor content changes
const TipTapEditorWithComments: React.FC<TipTapEditorProps> = ({
  initialContent = '',
  onChange,
  label,
  readOnly = false,
  placeholder,
  highlightedTextList = [],
  onCharacterCountChange,
  editorRef,
}) => {
  const innerEditorRef = useRef<Editor | null>(null);
  const _editorRef = editorRef || innerEditorRef;
  const { comments, activeCommentId, handleCommentClick } = useComments(_editorRef, highlightedTextList);
  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>
      <Stack
        sx={{
          flex: 1,
          '& .ProseMirror': {
            flex: 1,
            outline: 'none',
            padding: 2,
            overflowY: 'auto',
            minHeight: '100px',
            height: '100%',
            '&:focus': {
              outline: 'none',
            },
            // Positive feedback
            '& .tiptap-comment[data-comment-type="positive"]': {
              backgroundColor: 'rgba(76, 175, 80, 0.3)',
              borderRadius: '2px',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(76, 175, 80, 0.5)',
              },
            },
            // Active positive feedback
            '& .tiptap-comment[data-comment-type="positive"][data-comment-active="true"]': {
              backgroundColor: 'rgba(76, 175, 80, 0.6)',
            },
            // Negative feedback
            '& .tiptap-comment[data-comment-type="negative"]': {
              backgroundColor: 'rgba(244, 67, 54, 0.3)',
              borderRadius: '2px',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.5)',
              },
            },
            // Active negative feedback
            '& .tiptap-comment[data-comment-type="negative"][data-comment-active="true"]': {
              backgroundColor: 'rgba(244, 67, 54, 0.6)',
            },
            // Neutral feedback
            '& .tiptap-comment[data-comment-type="neutral"]': {
              backgroundColor: 'rgba(33, 150, 243, 0.3)',
              borderRadius: '2px',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(33, 150, 243, 0.5)',
              },
            },
            // Active neutral feedback
            '& .tiptap-comment[data-comment-type="neutral"][data-comment-active="true"]': {
              backgroundColor: 'rgba(33, 150, 243, 0.6)',
            },
            // Styles for the placeholder extension
            '& p.is-editor-empty:first-child::before': {
              color: 'rgba(0, 0, 0, 0.5)',
              content: 'attr(data-placeholder)',
              float: 'left',
              height: 0,
              pointerEvents: 'none',
            },
            // Styles for comment extension
            '& .tiptap-comment': {
              cursor: 'pointer',
            },
          },
        }}
      >
        {label && <Typography variant="subtitle1" mb={1}>{label}</Typography>}
        <Box
          sx={{
            display: 'flex',
            gap: 2
          }}
        >
          <Box sx={{ flex: '1 1 auto' }}>
            <InnerEditor
              initialContent={initialContent}
              editorRef={_editorRef}
              onChange={onChange}
              readOnly={readOnly}
              placeholder={placeholder}
              onHighlightClick={handleCommentClick}
              onCharacterCountChange={onCharacterCountChange}
            />
          </Box>
          {comments.length > 0 && (
            <Box
              sx={{
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                minWidth: '300px'
              }}
            >
              <Card
                sx={{
                  flex: '1 1 0px',
                  overflowY: 'auto'
                }}
              >
                <CommentManager
                  editorRef={_editorRef}
                  comments={comments}
                  activeCommentId={activeCommentId}
                  onCommentClick={handleCommentClick}
                />
              </Card>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default TipTapEditorWithComments; 